import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  TrendingUp, 
  Package, 
  RefreshCw,
  Loader2,
  Settings,
  BarChart3,
  Wallet,
  Megaphone,
  Truck
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

// Tab Components
import OverviewTab from '../../components/profitability/OverviewTab';
import InventoryTab from '../../components/profitability/InventoryTab';
import ExpensesTab from '../../components/profitability/ExpensesTab';
import SettingsTab from '../../components/profitability/SettingsTab';
import CampaignsTab from '../../components/profitability/CampaignsTab';
import TPLModule from '../Operations/TPLModule';

const ProfitabilityHub = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('monthly');
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(() => Math.ceil((new Date().getMonth() + 1) / 3));
  const [overviewData, setOverviewData] = useState(null);

  const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';

  // View options
  const viewOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'ytd', label: 'Year to Date' }
  ];

  // Generate period options (last 12 months)
  const periodOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    periodOptions.push({ value, label });
  }

  // Generate year options
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) {
    yearOptions.push({ value: y, label: String(y) });
  }

  // Fetch overview data based on view type
  const fetchOverview = async () => {
    try {
      setLoading(true);
      
      let params = new URLSearchParams();
      params.append('view', viewType);
      
      switch (viewType) {
        case 'daily':
          params.append('date', selectedDate);
          break;
        case 'monthly':
          params.append('year_month', selectedPeriod);
          break;
        case 'quarterly':
          params.append('year', selectedYear);
          params.append('quarter', selectedQuarter);
          break;
        case 'yearly':
          params.append('year', selectedYear);
          break;
        case 'ytd':
          params.append('year', selectedYear);
          break;
        // weekly uses defaults
      }
      
      const response = await api.get(`/api/v1/profitability/overview?${params.toString()}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      setOverviewData(response.data || response);
    } catch (error) {
      console.error('Error fetching profitability overview:', error);
      toast.error('Failed to load profitability data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [viewType, selectedPeriod, selectedDate, selectedYear, selectedQuarter]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'expenses', label: 'Expenses', icon: Wallet },
    { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
    { id: 'tpl', label: '3PL & Operations', icon: Truck },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Format currency
  const formatCurrency = (value, compact = false) => {
    const num = Number(value) || 0;
    if (compact && Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-[#0f1729] text-white p-4">
      {/* Compact Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Profitability Hub</h1>
              <p className="text-gray-400 text-xs">Track P&L, inventory costs, break-even</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View Type Selector */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-400">View:</span>
              <select
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
                className="bg-[#1a2744] border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                {viewOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Dynamic Period Selector based on view type */}
            {viewType === 'daily' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-[#1a2744] border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            )}
            
            {viewType === 'monthly' && (
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="bg-[#1a2744] border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                {periodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
            
            {viewType === 'quarterly' && (
              <div className="flex items-center gap-1">
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(Number(e.target.value))}
                  className="bg-[#1a2744] border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  <option value={1}>Q1</option>
                  <option value={2}>Q2</option>
                  <option value={3}>Q3</option>
                  <option value={4}>Q4</option>
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="bg-[#1a2744] border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  {yearOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            {(viewType === 'yearly' || viewType === 'ytd') && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="bg-[#1a2744] border border-gray-600 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              >
                {yearOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
            
            {overviewData && (
              <span className="text-[10px] text-gray-400 bg-[#1a2744] px-1.5 py-0.5 rounded">
                {overviewData.period || `${overviewData.days_with_data}d`}
              </span>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchOverview}
              disabled={loading}
              className="h-7 px-2 text-xs border-emerald-600 text-emerald-400 hover:bg-emerald-600/20"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Compact Tabs */}
      <div className="flex gap-0.5 mb-4 border-b border-gray-700">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-emerald-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <OverviewTab 
            data={overviewData} 
            loading={loading} 
            formatCurrency={formatCurrency}
            selectedPeriod={selectedPeriod}
          />
        )}
        {activeTab === 'campaigns' && (
          <CampaignsTab 
            tenantId={tenantId}
            selectedPeriod={selectedPeriod}
            onUpdate={fetchOverview}
            onPeriodChange={(month) => setSelectedPeriod(month)}
          />
        )}
        {activeTab === 'inventory' && (
          <InventoryTab 
            tenantId={tenantId}
            onUpdate={fetchOverview}
          />
        )}
        {activeTab === 'expenses' && (
          <ExpensesTab 
            tenantId={tenantId}
            selectedPeriod={selectedPeriod}
            onUpdate={fetchOverview}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsTab 
            tenantId={tenantId}
            onUpdate={fetchOverview}
          />
        )}
        {activeTab === 'tpl' && (
          <TPLModule tenantId={tenantId} />
        )}
      </div>
    </div>
  );
};

export default ProfitabilityHub;
